import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
  type ReactNode,
} from "react";
import { getProducts } from "@/lib/api";
import type { Product } from "@/lib/types";

type ProductContextValue = {
  products: Product[];
  loading: boolean;
  error: string | null;
  refreshProducts: () => Promise<void>;
};

const ProductContext = createContext<ProductContextValue>({
  products: [],
  loading: true,
  error: null,
  refreshProducts: async () => {},
});

export function ProductProvider({ children }: { children: ReactNode }) {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const mountedRef = useRef(true);
  const fetchingRef = useRef(false);

  const fetchAll = useCallback(async () => {
    if (fetchingRef.current) return;

    fetchingRef.current = true;

    if (mountedRef.current) {
      setLoading(true);
      setError(null);
    }

    try {
      const data = await getProducts();

      if (!mountedRef.current) return;

      setProducts(data);
    } catch (err) {
      if (!mountedRef.current) return;

      console.error("ProductContext:", err);
      setError("No pudimos cargar los productos.");
    } finally {
      if (mountedRef.current) {
        setLoading(false);
      }

      fetchingRef.current = false;
    }
  }, []);

  useEffect(() => {
    mountedRef.current = true;

    fetchAll();

    return () => {
      mountedRef.current = false;
    };
  }, [fetchAll]);

  const refreshProducts = useCallback(async () => {
    fetchingRef.current = false;
    await fetchAll();
  }, [fetchAll]);

  return (
    <ProductContext.Provider
      value={{
        products,
        loading,
        error,
        refreshProducts,
      }}
    >
      {children}
    </ProductContext.Provider>
  );
}

export function useProductContext() {
  return useContext(ProductContext);
}